import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from 'components/AppIcon';
import Image from 'components/AppImage';
import Button from 'components/ui/Button';

export default function ProductInfoPanel({ product, budgetData, onSaveWishlist, onProceed, onDismiss, savedToWishlist }) {
  if (!product) return null;

  const budgetRemaining = budgetData?.categoryBudget - budgetData?.categorySpent;
  const afterPurchase = budgetRemaining - product?.price;
  const canAfford = afterPurchase >= 0;
  const budgetPercent = Math.min(100, Math.round((budgetData?.categorySpent / budgetData?.categoryBudget) * 100));
  const afterPercent = Math.min(100, Math.round(((budgetData?.categorySpent + product?.price) / budgetData?.categoryBudget) * 100));

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: '100%', opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: '100%', opacity: 0 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className="absolute bottom-0 left-0 right-0 z-20 rounded-t-2xl overflow-hidden"
        style={{ background: 'rgba(15,23,42,0.97)', backdropFilter: 'blur(20px)', border: '1px solid rgba(6,182,212,0.2)', borderBottom: 'none' }}
      >
        {/* Drag handle */}
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 rounded-full bg-slate-600" />
        </div>

        <div className="px-4 md:px-6 pb-6 max-h-[70vh] overflow-y-auto">
          {/* Product header */}
          <div className="flex items-start gap-4 mb-4">
            <div className="w-16 h-16 md:w-20 md:h-20 rounded-xl overflow-hidden flex-shrink-0 bg-slate-800">
              <Image
                src={product?.image}
                alt={product?.imageAlt}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-heading font-semibold text-base md:text-lg text-slate-50 line-clamp-2">{product?.name}</h3>
              <p className="text-slate-400 text-sm mt-0.5">{product?.brand}</p>
              <div className="flex items-center gap-2 mt-1">
                <span className="font-data text-xl font-bold text-cyan-400">${product?.price?.toFixed(2)}</span>
                {product?.originalPrice && (
                  <span className="font-data text-sm text-slate-500 line-through">${product?.originalPrice?.toFixed(2)}</span>
                )}
                {product?.originalPrice && (
                  <span className="text-xs text-emerald-400 bg-emerald-400/10 px-2 py-0.5 rounded-full">
                    {Math.round((1 - product?.price / product?.originalPrice) * 100)}% off
                  </span>
                )}
              </div>
            </div>
            <button onClick={onDismiss} className="p-2 rounded-lg hover:bg-slate-800 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center">
              <Icon name="X" size={18} color="#94A3B8" />
            </button>
          </div>

          {/* Budget Impact */}
          <div className={`rounded-xl p-4 mb-4 border ${canAfford ? 'bg-emerald-500/10 border-emerald-500/20' : 'bg-red-500/10 border-red-500/20'}`}>
            <div className="flex items-center gap-2 mb-3">
              <Icon name={canAfford ? 'TrendingUp' : 'AlertTriangle'} size={18} color={canAfford ? '#10B981' : '#EF4444'} />
              <span className={`font-heading font-semibold text-sm ${canAfford ? 'text-emerald-400' : 'text-red-400'}`}>
                {canAfford ? 'Within Budget' : 'Exceeds Budget'}
              </span>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between text-xs text-slate-400 mb-1">
                <span>{budgetData?.category} Budget</span>
                <span className="font-data">${budgetData?.categoryBudget?.toFixed(2)}</span>
              </div>
              {/* Before bar */}
              <div>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-slate-500">Current spent</span>
                  <span className="font-data text-slate-400">${budgetData?.categorySpent?.toFixed(2)} ({budgetPercent}%)</span>
                </div>
                <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                  <div className="h-full bg-cyan-500 rounded-full transition-all" style={{ width: `${budgetPercent}%` }} />
                </div>
              </div>
              {/* After bar */}
              <div>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-slate-500">After purchase</span>
                  <span className={`font-data ${canAfford ? 'text-emerald-400' : 'text-red-400'}`}>
                    ${(budgetData?.categorySpent + product?.price)?.toFixed(2)} ({afterPercent}%)
                  </span>
                </div>
                <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full transition-all ${canAfford ? 'bg-emerald-500' : 'bg-red-500'}`} style={{ width: `${afterPercent}%` }} />
                </div>
              </div>
              <div className="flex justify-between pt-1 border-t border-slate-700">
                <span className="text-xs text-slate-400">Remaining after purchase</span>
                <span className={`font-data text-sm font-bold ${canAfford ? 'text-emerald-400' : 'text-red-400'}`}>
                  {canAfford ? `$${afterPurchase?.toFixed(2)}` : `-$${Math.abs(afterPurchase)?.toFixed(2)}`}
                </span>
              </div>
            </div>
          </div>

          {/* Price comparison */}
          <div className="mb-4">
            <h4 className="font-heading font-semibold text-sm text-slate-300 mb-2 flex items-center gap-2">
              <Icon name="BarChart2" size={16} color="#06B6D4" />
              Price Comparison
            </h4>
            <div className="space-y-2">
              {product?.priceComparison?.map((item, i) => (
                <div key={i} className="flex items-center justify-between bg-slate-800/60 rounded-lg px-3 py-2">
                  <span className="text-sm text-slate-300">{item?.store}</span>
                  <div className="flex items-center gap-2">
                    <span className={`font-data text-sm font-semibold ${item?.price === product?.price ? 'text-cyan-400' : 'text-slate-300'}`}>
                      ${item?.price?.toFixed(2)}
                    </span>
                    {item?.price === product?.price && (
                      <span className="text-xs bg-cyan-400/10 text-cyan-400 px-1.5 py-0.5 rounded">Current</span>
                    )}
                    {item?.price < product?.price && (
                      <span className="text-xs bg-emerald-400/10 text-emerald-400 px-1.5 py-0.5 rounded">
                        Save ${(product?.price - item?.price)?.toFixed(2)}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex gap-3">
            <Button
              variant="outline"
              size="default"
              iconName={savedToWishlist ? 'Heart' : 'Heart'}
              iconPosition="left"
              onClick={onSaveWishlist}
              className="flex-1"
            >
              {savedToWishlist ? 'Saved' : 'Wishlist'}
            </Button>
            <Button
              variant="default"
              size="default"
              iconName="ShoppingCart"
              iconPosition="left"
              onClick={onProceed}
              className="flex-1"
            >
              {canAfford ? 'Add to Cart' : 'Adjust Budget'}
            </Button>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}